import { clsx } from "clsx";
import { createContext, useContext } from "react";

import { useDebounce, useScrollDirection } from "@chef/hooks";
import { FEATURE_FLAGS } from "@chef/constants";
import { useFlag } from "@chef/feature-flags";

interface StickyProps {
  children: React.ReactNode;
  anchor?: "top" | "bottom";
  float?: boolean;
}

interface StickyContextProps {
  isFloating: boolean;
}

const TRESHOLD = 120;

const StickyContext = createContext<StickyContextProps>({
  isFloating: false,
});

export const useSticky = () => useContext(StickyContext);

export const Sticky = ({
  children,
  anchor = "top",
  float = false,
}: StickyProps) => {
  const { isAtTop, isAtBottom } = useScrollDirection(TRESHOLD);

  const { enabled: isWeekEditorRedesignEnabled } = useFlag(
    FEATURE_FLAGS.WEEK_EDITOR_REDESIGN,
  );

  let _isAtEdge: boolean;

  if (anchor === "top") {
    _isAtEdge = isAtTop;
  } else if (anchor === "bottom") {
    _isAtEdge = isAtBottom;
  } else {
    _isAtEdge = false;
  }

  const isAtEdge = useDebounce(_isAtEdge, 100);

  return (
    <div>
      <div
        key="sticky-dummy"
        aria-hidden="true"
        className={clsx("h-15", isWeekEditorRedesignEnabled && "md:h-30")}
      />
      <div
        key="sticky-content"
        className={clsx("fixed left-0 right-0 z-40", {
          "bg-background": isAtEdge,
          "top-0":
            (anchor === "top" && float && isAtEdge) ||
            (anchor === "top" && !float),
          "bottom-0": anchor === "bottom",
          "top-2 mx-4 lg:mx-8": anchor === "top" && float && !isAtEdge,
          "transition-all duration-300 ease-in-out": float,
        })}
      >
        <div
          className={clsx("w-full bg-background", {
            "px-4 md:px-6 shadow page-max-width":
              anchor === "top" && float && !isAtEdge,
            "shadow-fab": !float,
            "rounded-full": float && !isAtEdge,
          })}
        >
          <StickyContext.Provider value={{ isFloating: float && !isAtEdge }}>
            {children}
          </StickyContext.Provider>
        </div>
      </div>
    </div>
  );
};
